import { core } from '@tock/coretypes';
import { useFeatureFlag } from '@tock/shared/utils/featureFlagContext';
import React, { createContext, useContext } from 'react';

export const TaxStrategyContext = createContext<core.TaxStrategy | undefined>(undefined);

export const TaxStrategyContextProvider: React.FC<
  React.PropsWithChildren<{ children: React.ReactNode }>
> = ({ children }) => {
  const isTaxLibraryEnabled = useFeatureFlag('TAX_LIBRARY');
  const isTaxCenterEnabled = useFeatureFlag('TAX_CENTER');
  return (
    <TaxStrategyContext.Provider
      value={isTaxCenterEnabled ? 'TAX_CENTER' : isTaxLibraryEnabled ? 'TAX_LIBRARY' : 'SINGLE_TAX'}
    >
      {children}
    </TaxStrategyContext.Provider>
  );
};

type TaxStrategyOrStrategies = core.TaxStrategy | core.TaxStrategy[];

type TaxStrategyRenderProp = (args: {
  taxStrategy?: core.TaxStrategy;
  match?: boolean;
}) => React.ReactNode;

interface TaxStrategyChildrenProps {
  if: TaxStrategyOrStrategies;
  children: React.ReactNode | TaxStrategyRenderProp;
  doNotShow?: boolean;
  allowMigration?: boolean | string;
}

interface TaxStrategyRenderFunctionProps {
  children: TaxStrategyRenderProp;
}

type TaxStrategyProps = TaxStrategyChildrenProps | TaxStrategyRenderFunctionProps;

/**
 * Component for conditional or variable rendering of UI based on TaxStrategy.
 *
 * This component has two main APIs:
 * 1) Children props, which will render/not render based on the match of the tax strategy
 * 2) A render prop function, which will be called with results of the match, for the call site to use as wanted
 */
export const TaxStrategy: React.FC<TaxStrategyProps> = (props) => {
  const {
    if: strategyOrStrategiesBeingTested,
    children,
    doNotShow = false,
    allowMigration = false,
  } = props as TaxStrategyChildrenProps;

  const taxStrategy = useContext(TaxStrategyContext);
  const strategiesMatch = useIsTaxStrategy(strategyOrStrategiesBeingTested, { allowMigration });

  if (strategyOrStrategiesBeingTested === undefined && typeof children === 'function') {
    return children({ taxStrategy });
  }

  if (typeof children === 'function') {
    return children({ taxStrategy, match: strategiesMatch });
  }
  if (strategiesMatch && !doNotShow) {
    return <>{children}</>;
  } else if (!strategiesMatch && doNotShow) {
    return <>{children}</>;
  }
  return null;
};

function matches(
  strategyOrStrategiesBeingTested: TaxStrategyOrStrategies,
  taxStrategy: core.TaxStrategy | undefined
): boolean {
  if (taxStrategy === undefined) {
    return false;
  }
  return typeof strategyOrStrategiesBeingTested == 'object'
    ? strategyOrStrategiesBeingTested.includes(taxStrategy)
    : taxStrategy === strategyOrStrategiesBeingTested;
}

/**
 * React hook that returns the tax strategy, if known.
 */
export function useTaxStrategy(): core.TaxStrategy | undefined {
  return useContext(TaxStrategyContext);
}

/**
 * React hook that returns a boolean representing if the specific tax strategy provided is matched.
 *
 * `allowMigration` option is used to return a true result for a tax strategy
 * if the migration to that strategy has been enabled. How that is determined
 * is ad hoc and implemented in this hook.
 */
export function useIsTaxStrategy(
  strategyOrStrategiesBeingTested?: TaxStrategyOrStrategies,
  { allowMigration }: { allowMigration: boolean | string } = { allowMigration: false }
): boolean {
  const taxStrategy = useTaxStrategy();
  const isTaxCenterMigrationPeriodEnabled = useFeatureFlag(
    typeof allowMigration === 'string'
      ? (`TAX_CENTER_MIGRATION_${allowMigration}` as core.Feature.FeatureName)
      : 'TAX_CENTER_MIGRATION_PERIOD'
  );
  if (strategyOrStrategiesBeingTested === undefined) {
    return false;
  }

  const strategiesDirectlyMatch = matches(strategyOrStrategiesBeingTested, taxStrategy);
  const migrationMatch =
    matches(strategyOrStrategiesBeingTested, 'TAX_CENTER') && isTaxCenterMigrationPeriodEnabled;
  const strategiesMatch = strategiesDirectlyMatch || (!!allowMigration && migrationMatch);
  return strategiesMatch;
}
